/* Email wiring health widget shown on the Admin → Stats tab beside StripeHealthCard.
 * Surfaces: current active from-address & reply-to (whether from site_settings or env),
 * Resend API-key presence, and a one-tap "Send test email" button so admins can verify
 * DNS propagation the moment their Resend domain finishes verifying. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return "—"; }
}

export default function EmailHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-health");
      setData(data);
    } catch {
      toast.error("Could not load email health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []);

  const sendTest = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const body = testTo.trim() ? { to: testTo.trim() } : {};
      const { data } = await api.post("/admin/email-test", body);
      setLastResult(data);
      if (data.sent) {
        toast.success(`Test email sent to ${data.to} — check your inbox in ~30 s`);
      } else {
        toast.error(`Send failed: ${data.error || "unknown"}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed";
      toast.error(msg);
      setLastResult({ sent: false, error: msg });
    } finally {
      setSending(false);
    }
  };

  if (!data && loading) {
    return (
      <div className="card-recipe mb-8" data-testid="email-health-loading">
        <p className="overline mb-2 text-rf-orange">Email wiring</p>
        <p className="text-sm text-rf-ink-soft">Checking…</p>
      </div>
    );
  }
  if (!data) return null;

  const senderChipClass = !data.resend_api_key_present
    ? "!bg-rose-500/20 !text-rose-800"
    : data.is_sandbox
    ? "!bg-amber-400/20 !text-amber-900"
    : "!bg-emerald-500/20 !text-emerald-800";
  const senderLabel = !data.resend_api_key_present
    ? "NO API KEY"
    : data.is_sandbox
    ? "SANDBOX (deliverability limited)"
    : "CUSTOM DOMAIN ✓";

  return (
    <div className="card-recipe mb-8" data-testid="email-health-card">
      <div className="flex items-center justify-between mb-3">
        <p className="overline text-rf-orange">Email wiring</p>
        <button
          data-testid="email-health-refresh"
          onClick={fetchHealth}
          disabled={loading}
          className="text-xs font-bold text-rf-orange hover:underline disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div className="p-4 rounded-xl bg-rf-cream-alt sm:col-span-2" data-testid="email-health-from">
          <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-1">Active FROM address</p>
          <p className="font-mono text-sm text-rf-ink break-all mb-1.5">{data.active_from}</p>
          <span className={`chip ${senderChipClass}`}>{senderLabel}</span>
          <span className="text-[11px] text-rf-ink-soft ml-2">source: <b>{data.source_from}</b></span>
        </div>
        <div className="p-4 rounded-xl bg-rf-cream-alt" data-testid="email-health-reply">
          <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-1">Reply-To</p>
          <p className="font-mono text-xs text-rf-ink break-all mb-1.5">{data.active_reply_to || "— (none set)"}</p>
          {data.active_reply_to && (
            <span className="text-[11px] text-rf-ink-soft">source: <b>{data.source_reply_to}</b></span>
          )}
        </div>
      </div>

      {(!data.resend_api_key_present || data.is_sandbox) && (
        <div className="p-3 rounded-xl bg-amber-100 border border-amber-300 text-xs text-amber-900 mb-5" data-testid="email-health-warning">
          {!data.resend_api_key_present && (
            <div>⚠ <b>RESEND_API_KEY missing.</b> Emails will be logged to backend logs instead of delivered. Set the key in the pod .env and restart.</div>
          )}
          {data.resend_api_key_present && data.is_sandbox && (
            <div>
              ⚠ <b>You&apos;re using Resend&apos;s sandbox sender</b> (<code>onboarding@resend.dev</code>). It only delivers to <i>your own verified Resend account email</i> — gift codes to strangers won&apos;t arrive.
              <br />
              <span className="italic">Fix once your DNS verification finishes:</span> go to <b>Admin › Site Config → Email sender</b> and set the FROM address to <code>Recipe Filer &lt;hello@send.recipefiler.com&gt;</code> (or whichever subdomain you configured in Resend).
            </div>
          )}
        </div>
      )}

      <div className="border-t border-rf-ink/5 pt-4">
        <p className="text-[11px] font-bold text-rf-ink-soft uppercase tracking-wider mb-2">Send a test email</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            inputMode="email"
            placeholder="(defaults to your admin email)"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="input-rf flex-1"
            data-testid="email-test-to"
          />
          <button
            onClick={sendTest}
            disabled={sending || !data.resend_api_key_present}
            data-testid="email-test-send"
            className="btn-amber disabled:opacity-50 justify-center whitespace-nowrap"
          >
            {sending ? "Sending…" : "Send test →"}
          </button>
        </div>
        {lastResult && (
          <div className={`mt-3 p-3 rounded-xl text-xs ${lastResult.sent ? "bg-emerald-100 border border-emerald-300 text-emerald-900" : "bg-rose-100 border border-rose-300 text-rose-900"}`} data-testid="email-test-result">
            {lastResult.sent ? (
              <span>✔ Sent to <b>{lastResult.to}</b> · Resend id <code className="text-[10px]">{lastResult.email_id}</code></span>
            ) : (
              <span>✖ {lastResult.error || "Send failed. Check the Stripe/Email API key or Resend dashboard for details."}</span>
            )}
          </div>
        )}
        {data.last_email_test_at && !lastResult && (
          <p className="mt-2 text-[11px] text-rf-ink-soft">Last test: {fmtDate(data.last_email_test_at)}</p>
        )}
      </div>
    </div>
  );
}
